import React, { useState, useEffect, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { createPortal } from 'react-dom';
import MLIcon from '../MLIcon';
import ModalHeader from './ModalHeader';
import ModalContent from './ModalContent';
import ModalFooter from './ModalFooter';
import './Modal.css';

const Modal = ({
  className,
  children,
  closeIcon = true,
  closeIconTestId,
  trigger,
  open: controlledOpen,
  firstElement,
  onClosed,
  ...otherProps
}) => {
  const [isOpen, setIsOpen] = useState(controlledOpen || false);
  const [activeTrap, setActiveTrap] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const modalRef = useRef(null);
  const previousActiveElement = useRef(null);

  // Use controlled or uncontrolled state
  const open = controlledOpen === undefined ? isOpen : controlledOpen;

  useEffect(() => {
    if (controlledOpen !== undefined) {
      setIsOpen(controlledOpen);
    }
  }, [controlledOpen]);

  useEffect(() => {
    if (open) {
      previousActiveElement.current = document.activeElement;
      document.body.style.overflow = 'hidden';

      // Start animation sequence
      setIsAnimating(true);
      requestAnimationFrame(() => {
        setIsVisible(true);
        setActiveTrap(true);
      });

      // Focus management
      setTimeout(() => {
        if (firstElement && modalRef.current) {
          const element = modalRef.current.querySelector(firstElement);
          if (element) {
            element.focus();
          }
        } else if (modalRef.current) {
          const focusableElements = modalRef.current.querySelectorAll(
            'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
          );
          if (focusableElements.length > 0) {
            focusableElements[0].focus();
          }
        }
      }, 100);
    } else {
      setIsVisible(false);
      setActiveTrap(false);

      // Wait for animation to complete before hiding
      setTimeout(() => {
        setIsAnimating(false);
        document.body.style.overflow = '';

        // Restore focus to previous element
        if (previousActiveElement.current?.focus) {
          previousActiveElement.current.focus();
        }
      }, 300);
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [open, firstElement]);

  // Focus trap implementation
  const handleKeyDown = useCallback(
    (e) => {
      if (!activeTrap || !modalRef.current) return;

      if (e.key === 'Escape') {
        handleClose();
        return;
      }

      if (e.key === 'Tab') {
        const focusableElements = modalRef.current.querySelectorAll(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        );

        if (focusableElements.length === 0) return;

        const firstElement = focusableElements[0];
        const lastElement = focusableElements[focusableElements.length - 1];

        if (e.shiftKey && document.activeElement === firstElement) {
          e.preventDefault();
          lastElement.focus();
        } else if (!e.shiftKey && document.activeElement === lastElement) {
          e.preventDefault();
          firstElement.focus();
        }
      }
    },
    [activeTrap]
  );

  const handleOpen = () => {
    if (controlledOpen === undefined) {
      setIsOpen(true);
    }
  };

  const handleClose = () => {
    if (controlledOpen === undefined) {
      setIsOpen(false);
    }
    if (onClosed) {
      onClosed();
    }
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  const renderCloseIcon = () => {
    if (closeIcon === true) {
      return (
        <button
          onClick={handleClose}
          className="close"
          aria-label="close"
          data-test-id={closeIconTestId}
        >
          <MLIcon
            fill="#383838"
            type="cancel"
            width="18"
            height="18"
            scale={0.8}
            viewBox="0 0 18 18"
            className="icon"
          />
        </button>
      );
    } else if (closeIcon === false) {
      return null;
    }

    return (
      <button
        onClick={handleClose}
        className="close"
        aria-label="close"
        data-test-id={closeIconTestId}
      >
        {closeIcon}
      </button>
    );
  };

  const clonedTrigger = trigger
    ? React.cloneElement(trigger, {
        onClick: handleOpen,
        'aria-haspopup': 'dialog',
        'aria-expanded': open,
      })
    : null;

  const modalClasses = classNames(
    'ui modal',
    'ng-ui-modal',
    {
      visible: isVisible,
      active: isVisible,
    },
    className
  );

  const dimmerClasses = classNames('ui page modals dimmer transition', {
    visible: isVisible,
    active: isVisible,
  });

  if (!open && !isAnimating) {
    return clonedTrigger;
  }

  return (
    <>
      {clonedTrigger}
      {createPortal(
        <div
          className={dimmerClasses}
          onClick={handleBackdropClick}
          role="presentation"
        >
          <dialog
            ref={modalRef}
            className={modalClasses}
            aria-modal="true"
            onKeyDown={handleKeyDown}
            {...otherProps}
          >
            <div id="focus-trap-modal">
              {renderCloseIcon()}
              {children}
            </div>
          </dialog>
        </div>,
        document.body
      )}
    </>
  );
};

Modal.propTypes = {
  className: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.array,
    PropTypes.object,
  ]),
  children: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.array,
    PropTypes.object,
    PropTypes.node,
  ]),
  closeIcon: PropTypes.oneOfType([PropTypes.bool, PropTypes.element]),
  closeIconTestId: PropTypes.string,
  trigger: PropTypes.element,
  open: PropTypes.bool,
  firstElement: PropTypes.string,
  onClosed: PropTypes.func,
};

Modal.Header = ModalHeader;
Modal.Content = ModalContent;
Modal.Footer = ModalFooter;

export default Modal;
